import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";

/**
 * Màn hình nhận kết quả OAuth TikTok Business — route /tiktok/callback
 * (phải trùng TIKTOK_FRONTEND_CALLBACK_URL cấu hình ở backend).
 *
 * Backend redirect về đây kèm query theo tài liệu:
 *   status         success | error
 *   account_id     ID tài khoản OAuth TikTok (khi thành công)
 *   connection_id  ID kênh kết nối vừa tạo/cập nhật (khi thành công)
 *   message        nội dung lỗi (khi thất bại)
 *
 * Thành công: báo thành công rồi quay về màn hình quản lý kênh để danh sách
 * kết nối được nạp lại. Thất bại: giữ màn hình cho người dùng đọc lỗi rồi thử lại.
 */

const AUTO_BACK_SECONDS = 3;

export default function TiktokCallbackStatus() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const status = params.get("status");
    const errorMessage = params.get("message");
    const accountId = params.get("account_id");
    const connectionId = params.get("connection_id");
    const success = status === "success";
    const [countdown, setCountdown] = useState(AUTO_BACK_SECONDS);

    useEffect(() => {
        if (!success) return;
        // Mở dạng popup -> đóng luôn để tab cha tự nạp lại danh sách kết nối
        if (window.opener && window.opener !== window) {
            try {
                window.opener.postMessage({ source: "tiktok-oauth", status: "success", accountId, connectionId }, window.location.origin);
            } catch { /* khác origin thì bỏ qua, tab cha vẫn refresh khi quay lại */ }
            window.close();
            return;
        }
        const tick = setInterval(() => setCountdown((s) => s - 1), 1000);
        const timer = setTimeout(() => navigate("/chats", { replace: true }), AUTO_BACK_SECONDS * 1000);
        return () => {
            clearInterval(tick);
            clearTimeout(timer);
        };
    }, [success, accountId, connectionId, navigate]);

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center max-w-md w-full">

                <div className="mb-4">
                    {success ? (
                        <CheckCircleFilled className="text-5xl text-emerald-500" />
                    ) : (
                        <CloseCircleFilled className="text-5xl text-red-500" />
                    )}
                </div>

                <h1 className="text-xl font-bold text-slate-800 mb-2">
                    {success ? "Liên kết TikTok Business thành công." : "Liên kết TikTok Business thất bại"}
                </h1>

                <p className="text-sm text-slate-500">
                    {success
                        ? `Đang quay lại màn hình quản lý kết nối${countdown > 0 ? ` sau ${countdown}s` : ""}...`
                        : (errorMessage || "Không lấy được quyền từ TikTok, vui lòng thử kết nối lại.")}
                </p>

                {success && connectionId && (
                    <p className="text-xs text-slate-400 mt-2">
                        Mã kênh kết nối: #{connectionId}
                    </p>
                )}

                <Button
                    type="primary"
                    className="mt-6"
                    onClick={() => navigate("/chats", { replace: true })}
                >
                    {success ? "Quay lại ngay" : "Thử kết nối lại"}
                </Button>

            </div>
        </div>
    );
}
